import { Container } from '@/components/ui/Grid';
import EditorOutput from '@/components/common/EditorOutput';
import MotionHeader from '@/components/ui/MotionHeader';
import styles from './category.module.scss';
import Link from 'next/link';
import { ProductsFilter } from '@/components/Category/ProductsFilter/ProductsFilter';
import { useCallback, useMemo, useState } from 'react';
import { ProductFilterButtons } from '@/components/Category/ProductsFilter/ProductFilterButtons';
import Modal from '@/components/Modal';
import RequestPriceForm from '@/components/Category/RequestPriceForm';
import { useTranslation } from 'next-i18next';

const Product = ({ product }) => {
  return (
    <Link href={`/catalog/product/${product.id}`}>
      <a className={styles.category}>
        <div className={styles.productName}>{product.title}</div>
        <div className={styles.categoryPhoto}>
          {product.image && product.image.length > 0 && (
            <img src={product.image[0].image} alt={product.title} />
          )}
        </div>
      </a>
    </Link>
  );
};

const Category = ({ data = { products: [], categories: [] } }) => {
  const [filteredProducts, setFilteredProducts] = useState(data.products);
  const [activeFilters, setActiveFilters] = useState({});
  const [displayFiltersMobile, setDisplayFiltersMobile] = useState(false);
  const [sendPriceModalIsOpen, setSendPriceModalIsOpen] = useState(false);
  const { t } = useTranslation(['common', 'products']);

  const allowedFilters = useMemo(() => {
    const filteredProductsIds = filteredProducts.map(({ id }) => id);
    const firstCategoryFilters = data.filters.find(({ field }) => field === 'category.1');
    const secondCategoryFilters = data.filters.find(({ field }) => field === 'category.2');
    const result = {
      'category.1': activeFilters['category.2']?.length
        ? firstCategoryFilters.values
            .filter(({ products }) => filteredProductsIds.filter(x => products.includes(x)).length)
            .map(({ id }) => id)
        : firstCategoryFilters?.values.map(({ id }) => id),
      'category.2': activeFilters['category.1']?.length
        ? secondCategoryFilters?.values
            .filter(({ products }) => filteredProductsIds.filter(x => products.includes(x)).length)
            .map(({ id }) => id)
        : secondCategoryFilters?.values.map(({ id }) => id),
    };
    data.filters.forEach(({ field, values }) => {
      if (!field.includes('category')) {
        result[field] = values
          .filter(({ id }) =>
            filteredProducts.some(product => product[field].map(item => item.id).includes(id))
          )
          .map(({ id }) => id);
      }
    });
    return result;
  }, [data.filters, filteredProducts, activeFilters]);

  const handleChangeFilter = useCallback(
    filters => {
      setActiveFilters(filters);
      if (!Object.values(filters).some(value => value.length)) {
        setFilteredProducts(data.products);
        return;
      }
      let filteredProductsIds = [];
      Object.entries(filters).forEach(([field, filterValue]) => {
        if (field.includes('category')) {
          filterValue.forEach(
            filterValueId =>
              (filteredProductsIds = [
                ...filteredProductsIds,
                ...data.filters
                  .find(filt => filt.field === field)
                  .values.find(val => val.id === filterValueId).products,
              ])
          );
        } else {
          filteredProductsIds = [
            ...filteredProductsIds,
            ...data.products
              .filter(
                product =>
                  !!filterValue.filter(x => product[field].map(item => item.id).includes(x)).length
              )
              .map(({ id }) => id),
          ];
        }
      });
      setFilteredProducts(data.products.filter(({ id }) => filteredProductsIds.includes(id)));
    },
    [setActiveFilters, setFilteredProducts, data.products, data.filters]
  );

  return (
    <Container>
      {data.title && <MotionHeader className={styles.header}>{data.title}</MotionHeader>}
      {data.text && <EditorOutput className={styles.descriptionText} data={data.text} />}
      {data.categories && (
        <div className={styles.categoriesNav}>
          {data.categories?.map(category => (
            <div key={category.id} className={styles.categoryName}>
              <Link href={`/catalog/category/${category.id}`}>
                <a
                  className={
                    data.id === category.id
                      ? `${styles.categoryLink} ${styles.activeCategoryLink}`
                      : styles.categoryLink
                  }
                >
                  {category.title}
                </a>
              </Link>
            </div>
          ))}
        </div>
      )}
      <div className={styles.wrapper}>
        <div className={styles.productsFilter}>
          <ProductsFilter
            filters={data.filters}
            activeFilters={activeFilters}
            allowedFilters={allowedFilters}
            displayFiltersMobile={displayFiltersMobile}
            onCloseMobileFilters={() => setDisplayFiltersMobile(false)}
            onChangeFilter={handleChangeFilter}
          />
          <ProductFilterButtons
            onToggleFilters={() => setDisplayFiltersMobile(true)}
            onTogglePriceModal={() => setSendPriceModalIsOpen(true)}
          />
          {sendPriceModalIsOpen && (
            <Modal
              showCloseButton={false}
              width={600}
              minHeight={460}
              close={() => setSendPriceModalIsOpen(false)}
            >
              <div className={styles.modalHeader}>{t('Заполните поля и мы вышлем вам прайс')}</div>
              <RequestPriceForm
                requestData={{ category: data.id }}
                onSuccess={() => setSendPriceModalIsOpen(false)}
                onClose={() => setSendPriceModalIsOpen(false)}
              />
            </Modal>
          )}
        </div>
        {filteredProducts && filteredProducts.length > 0 && (
          <div className={styles.productGrid}>
            {filteredProducts.map(c => (
              <div key={c.id} className={styles.productTile}>
                <Product product={c} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
};

export default Category;
